import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Download,
  ExternalLink,
  ImageOff,
  ImagePlus,
  RefreshCw,
  SearchX,
  TriangleAlert,
  Unplug
} from 'lucide-react'
import type { JobDTO, StartGenerateRequest, StartSaveRequest } from '@shared/types'
import { ImageStatus, JobStatus, JobType, MEDIA_FILTER_LABELS, isJobActive } from '@shared/types'
import { Button, Card, EmptyState, Skeleton, useToast } from '@/components/ui'
import PageHeader from '@/components/layout/PageHeader'
import { useRegisterShortcuts } from '@/components/layout/KeyboardShortcuts'
import { api, errorMessage } from '@/lib/api'
import { formatNumber, pluralize, relativeTime } from '@/lib/format'
import { useJobStore } from '@/stores/useJobStore'
import { useMediaStore } from '@/stores/useMediaStore'
import { useSitesStore } from '@/stores/useSitesStore'
import type { MediaAction } from './BatchActionBar'
import { BatchActionBar } from './BatchActionBar'
import { ImageGrid } from './ImageGrid'
import { JobProgressBar } from './JobProgressBar'
import { MediaToolbar } from './MediaToolbar'
import { Pagination } from './Pagination'
import { SaveConfirmDialog } from './SaveConfirmDialog'

/** How long a finished job's summary stays on screen before it clears itself. */
const COMPLETION_LINGER_MS = 9000

interface BulkResponse {
  updated: number
}

function HeaderSkeleton(): ReactElement {
  return (
    <div className="flex flex-col gap-5">
      <div className="space-y-3">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-4 w-72" />
      </div>
      <Skeleton className="h-10 w-full" />
      <ImageGrid items={[]} siteUrl="" loading siteBusy={false} savingActive={false} />
    </div>
  )
}

export default function MediaPage(): ReactElement {
  const { siteId } = useParams<{ siteId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const site = useSitesStore((state) => state.sites.find((entry) => entry.id === siteId) ?? null)
  const sitesLoading = useSitesStore((state) => state.loading)
  const loadSites = useSitesStore((state) => state.load)

  const items = useMediaStore((state) => state.items)
  const counts = useMediaStore((state) => state.counts)
  const page = useMediaStore((state) => state.page)
  const perPage = useMediaStore((state) => state.perPage)
  const total = useMediaStore((state) => state.total)
  const totalPages = useMediaStore((state) => state.totalPages)
  const filter = useMediaStore((state) => state.filter)
  const sort = useMediaStore((state) => state.sort)
  const query = useMediaStore((state) => state.query)
  const loading = useMediaStore((state) => state.loading)
  const mediaError = useMediaStore((state) => state.error)
  const selectedCount = useMediaStore((state) => state.selected.size)
  const openSite = useMediaStore((state) => state.open)
  const reload = useMediaStore((state) => state.reload)
  const setPage = useMediaStore((state) => state.setPage)
  const setFilter = useMediaStore((state) => state.setFilter)
  const setSort = useMediaStore((state) => state.setSort)
  const setQuery = useMediaStore((state) => state.setQuery)
  const selectAll = useMediaStore((state) => state.selectAll)
  const clearSelection = useMediaStore((state) => state.clearSelection)

  const activeJob = useJobStore((state) => (siteId ? state.activeForSite(siteId) : null))
  const loadJobs = useJobStore((state) => state.load)
  const cancelJob = useJobStore((state) => state.cancel)
  const applyJobEvent = useJobStore((state) => state.applyEvent)

  const [watchedJobId, setWatchedJobId] = useState<string | null>(null)
  const [pending, setPending] = useState<MediaAction | null>(null)
  const [saveOpen, setSaveOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const sitesRequested = useRef(false)

  useEffect(() => {
    if (!siteId) return
    setWatchedJobId(null)
    void openSite(siteId)
    void loadJobs(siteId).catch(() => {
      // The SSE `hello` frame re-sends active jobs, so a failed probe self-heals.
    })
  }, [siteId, openSite, loadJobs])

  // Landing straight on this URL means the sites list was never loaded.
  useEffect(() => {
    if (site || sitesRequested.current) return
    sitesRequested.current = true
    void loadSites()
  }, [site, loadSites])

  useEffect(() => {
    if (activeJob) setWatchedJobId(activeJob.id)
  }, [activeJob])

  const watchedJob = useJobStore((state) =>
    watchedJobId ? (state.jobs[watchedJobId] ?? null) : null
  )

  // A finished job stays visible for a moment so its outcome can be read.
  useEffect(() => {
    if (!watchedJob || isJobActive(watchedJob)) return
    const timer = window.setTimeout(() => setWatchedJobId(null), COMPLETION_LINGER_MS)
    return () => window.clearTimeout(timer)
  }, [watchedJob])

  const jobActive = activeJob !== null
  const fetchRunning = activeJob?.type === JobType.Fetch
  const savingActive = activeJob?.type === JobType.Save
  const needsSaveCount = counts?.['needs-save'] ?? 0
  const siteUrl = site?.siteUrl.replace(/\/+$/, '') ?? ''
  const installName = site?.installName ?? ''

  const eligibleIds = useMemo(
    () =>
      items
        .filter((item) => item.status === ImageStatus.Idle || item.status === ImageStatus.Failed)
        .filter((item) => item.generatedAlt === null)
        .map((item) => item.id),
    [items]
  )

  const failedIds = useMemo(
    () => items.filter((item) => item.status === ImageStatus.Failed).map((item) => item.id),
    [items]
  )

  const run = (action: MediaAction, task: () => Promise<void>): void => {
    if (pending !== null) return
    setPending(action)
    void task().finally(() => setPending(null))
  }

  const startJob = async (path: string, body?: unknown): Promise<JobDTO> => {
    const job = await api.post<JobDTO>(path, body)
    applyJobEvent({ type: 'job', job })
    setWatchedJobId(job.id)
    return job
  }

  const handleFetch = (): void => {
    if (!siteId) return
    run('fetch', async () => {
      try {
        await startJob(`/sites/${siteId}/fetch`)
        toast({
          title: 'Reading the media library',
          description: 'Argus is querying WordPress over SSH. Large libraries take a minute.',
          variant: 'info'
        })
      } catch (error) {
        toast({
          title: 'Could not start the fetch',
          description: errorMessage(error),
          variant: 'error'
        })
      }
    })
  }

  const startGenerate = (action: MediaAction, body: StartGenerateRequest, count: number): void => {
    if (!siteId) return
    run(action, async () => {
      try {
        const job = await startJob(`/sites/${siteId}/generate`, body)
        // A generate needs a free slot, so it may land QUEUED with nothing started.
        // Claiming otherwise would have the user watching for progress that is not
        // coming yet — the queue position is on the bar above the grid.
        const queued = job.status === JobStatus.Queued
        toast({
          title: queued
            ? `${formatNumber(count)} ${pluralize(count, 'image')} queued for alt text`
            : `Generating alt text for ${formatNumber(count)} ${pluralize(count, 'image')}`,
          description: queued
            ? 'Every generation slot is busy, so this is waiting in line. Its place is shown above the grid and it starts on its own — you can leave this page.'
            : 'Progress appears above the grid, and you can leave this page.',
          variant: 'info'
        })
      } catch (error) {
        toast({
          title: 'Could not start generating',
          description: errorMessage(error),
          variant: 'error'
        })
      }
    })
  }

  const handleGeneratePage = (): void => {
    if (jobActive) return
    if (eligibleIds.length === 0) {
      toast({
        title: 'Nothing to generate on this page',
        description: 'Everything here already has generated alt text. Use Regenerate on a card.',
        variant: 'info'
      })
      return
    }
    startGenerate('generate-page', { imageIds: eligibleIds }, eligibleIds.length)
  }

  // "All" is resolved server-side from the filter, so it is not capped by the page.
  const handleGenerateAll = (): void => {
    if (jobActive || total === 0) return
    startGenerate('generate-all', { filter }, total)
  }

  const handleRetryFailed = (): void => {
    if (jobActive || failedIds.length === 0) return
    startGenerate('retry-failed', { imageIds: failedIds, regenerate: true }, failedIds.length)
  }

  const handleCancel = (): void => {
    const job = activeJob ?? watchedJob
    if (!job || !isJobActive(job)) return
    run('cancel', async () => {
      try {
        await cancelJob(job.id)
        toast({
          title: 'Cancelling',
          description: 'The job stops after the item currently in flight.',
          variant: 'info'
        })
      } catch (error) {
        toast({
          title: 'Could not cancel that job',
          description: errorMessage(error),
          variant: 'error'
        })
      }
    })
  }

  const approve = (
    action: MediaAction,
    body: { imageIds?: string[]; filter?: string },
    emptyHint: string
  ): void => {
    if (!siteId) return
    run(action, async () => {
      try {
        const result = await api.post<BulkResponse>('/media/bulk', {
          siteId,
          action: 'approve',
          ...body
        })
        clearSelection()
        await reload()
        toast(
          result.updated > 0
            ? {
                title: `Approved ${formatNumber(result.updated)} ${pluralize(result.updated, 'image')}`,
                variant: 'success'
              }
            : { title: 'Nothing to approve', description: emptyHint, variant: 'info' }
        )
      } catch (error) {
        toast({
          title: 'Could not approve those images',
          description: errorMessage(error),
          variant: 'error'
        })
      }
    })
  }

  const handleApproveSelected = (): void => {
    const ids = [...useMediaStore.getState().selected]
    if (ids.length === 0) {
      toast({
        title: 'Nothing selected',
        description: 'Tick the images you want to approve, then press A.',
        variant: 'info'
      })
      return
    }
    approve(
      'approve-selected',
      { imageIds: ids },
      'Those images need alt text before they can be approved.'
    )
  }

  const handleApproveAll = (): void => {
    if (total === 0) return
    approve(
      'approve-all',
      { filter },
      `Nothing matching “${MEDIA_FILTER_LABELS[filter]}” has alt text to approve yet.`
    )
  }

  const handleOpenSave = (): void => {
    if (needsSaveCount === 0) {
      toast({
        title: 'Nothing to save',
        description: 'Approve some alt text first — only approved, unsaved images are written.',
        variant: 'info'
      })
      return
    }
    setSaveOpen(true)
  }

  const handleSaveConfirmed = (): void => {
    if (!siteId) return
    const count = needsSaveCount
    run('save', async () => {
      try {
        // No `imageIds` means "everything approved and not yet saved".
        const body: StartSaveRequest = {}
        await startJob(`/sites/${siteId}/save`, body)
        setSaveOpen(false)
        toast({
          title: `Writing ${formatNumber(count)} alt ${pluralize(count, 'tag')} to WordPress`,
          variant: 'info'
        })
      } catch (error) {
        toast({
          title: 'Could not start the save',
          description: errorMessage(error),
          variant: 'error'
        })
      }
    })
  }

  useRegisterShortcuts({
    focusSearch: () => searchRef.current?.focus(),
    generatePage: handleGeneratePage,
    approveSelected: handleApproveSelected,
    save: handleOpenSave
  })

  if (!site) {
    if (siteId && (sitesLoading || !sitesRequested.current)) return <HeaderSkeleton />
    return (
      <Card>
        <EmptyState
          icon={<Unplug />}
          title="Site not available"
          description="This site is not connected to your account any more, or the link is out of date."
          action={<Button onClick={() => navigate('/sites')}>Back to sites</Button>}
        />
      </Card>
    )
  }

  const neverFetched = site.lastFetchedAt === null
  const filtering = filter !== 'all' || query.trim().length > 0
  const hasLibrary = site.imageCount > 0 || total > 0 || items.length > 0
  const showSkeletons = (loading || fetchRunning) && items.length === 0
  const showEmpty = !showSkeletons && items.length === 0
  const from = total === 0 ? 0 : (page - 1) * perPage + 1
  const to = Math.min(page * perPage, total)

  let empty: ReactNode = null
  if (showEmpty && neverFetched) {
    empty = (
      <EmptyState
        icon={<ImagePlus />}
        title="No media fetched yet"
        description={`Argus reads ${site.installName}'s media library over SSH. Fetch it once, then generate alt text for everything missing it.`}
        action={
          <Button
            iconLeft={<Download />}
            onClick={handleFetch}
            loading={pending === 'fetch'}
            disabled={jobActive}
          >
            Fetch images
          </Button>
        }
      />
    )
  } else if (showEmpty && filtering) {
    empty = (
      <EmptyState
        icon={<SearchX />}
        title="No images match this view"
        description={
          query.trim()
            ? `Nothing in “${MEDIA_FILTER_LABELS[filter]}” matches “${query.trim()}”.`
            : `No image is currently in “${MEDIA_FILTER_LABELS[filter]}”.`
        }
        action={
          <div className="flex flex-wrap justify-center gap-2">
            {filter !== 'all' ? (
              <Button variant="secondary" onClick={() => setFilter('all')}>
                Clear filter
              </Button>
            ) : null}
            {query.trim() ? (
              <Button variant="secondary" onClick={() => setQuery('')}>
                Clear search
              </Button>
            ) : null}
          </div>
        }
      />
    )
  } else if (showEmpty) {
    empty = (
      <EmptyState
        icon={<ImageOff />}
        title="This library has no images"
        description={`${site.installName} has no image attachments in its WordPress media library. Upload some, then fetch again.`}
        action={
          <Button
            variant="secondary"
            iconLeft={<RefreshCw />}
            onClick={handleFetch}
            loading={pending === 'fetch'}
            disabled={jobActive}
          >
            Fetch again
          </Button>
        }
      />
    )
  }

  return (
    <>
      <PageHeader
        backTo="/sites"
        backLabel="Sites"
        eyebrow="Media library"
        title={site.installName}
        documentTitle={site.installName}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {siteUrl ? (
              <a
                href={siteUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 font-medium text-accent hover:underline"
              >
                {siteUrl.replace(/^https?:\/\//, '')}
                <ExternalLink className="size-3.5" aria-hidden="true" />
              </a>
            ) : null}
            <span>
              {formatNumber(site.imageCount)} {pluralize(site.imageCount, 'image')}
            </span>
            <span className="text-fg-subtle">
              {site.lastFetchedAt ? `Fetched ${relativeTime(site.lastFetchedAt)}` : 'Never fetched'}
            </span>
          </span>
        }
        actions={
          <Button
            variant="secondary"
            iconLeft={<RefreshCw />}
            onClick={handleFetch}
            loading={pending === 'fetch'}
            disabled={jobActive}
            title={jobActive ? 'Another job is running for this site' : undefined}
          >
            {neverFetched ? 'Fetch images' : 'Re-fetch library'}
          </Button>
        }
      />

      <div className="flex flex-col gap-5">
        {mediaError ? (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-card border border-bad-500/40 bg-bad-100/70 px-4 py-3 text-sm text-bad-700 dark:bg-bad-950/60 dark:text-bad-300"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>{mediaError}</span>
          </div>
        ) : null}

        {watchedJob ? (
          <JobProgressBar
            job={watchedJob}
            onCancel={handleCancel}
            cancelling={pending === 'cancel'}
          />
        ) : null}

        {hasLibrary ? (
          <>
            <MediaToolbar
              filter={filter}
              sort={sort}
              query={query}
              counts={counts}
              onFilterChange={setFilter}
              onSortChange={setSort}
              onQueryChange={setQuery}
              searchRef={searchRef}
            />

            <BatchActionBar
              filterLabel={MEDIA_FILTER_LABELS[filter]}
              matchingTotal={total}
              pageEligible={eligibleIds.length}
              failedOnPage={failedIds.length}
              selectedCount={selectedCount}
              pageCount={items.length}
              needsSaveCount={needsSaveCount}
              jobActive={jobActive}
              jobQueued={watchedJob?.status === JobStatus.Queued}
              pending={pending}
              onGeneratePage={handleGeneratePage}
              onGenerateAll={handleGenerateAll}
              onCancel={handleCancel}
              onRetryFailed={handleRetryFailed}
              onSelectAll={selectAll}
              onClearSelection={clearSelection}
              onApproveSelected={handleApproveSelected}
              onApproveAll={handleApproveAll}
              onSave={handleOpenSave}
            />
          </>
        ) : null}

        {showEmpty ? (
          <Card>{empty}</Card>
        ) : (
          <>
            {items.length > 0 ? (
              <p aria-live="polite" className="text-xs text-fg-muted">
                Showing {formatNumber(from)}–{formatNumber(to)} of {formatNumber(total)}{' '}
                {pluralize(total, 'image')}
                {query.trim() ? ` matching “${query.trim()}”` : ''}
                {selectedCount > 0 ? ` · ${formatNumber(selectedCount)} selected` : ''}
              </p>
            ) : null}

            <ImageGrid
              items={items}
              siteUrl={siteUrl}
              loading={showSkeletons || loading}
              siteBusy={jobActive}
              savingActive={savingActive}
            />

            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              disabled={loading}
            />
          </>
        )}
      </div>

      <SaveConfirmDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        onConfirm={handleSaveConfirmed}
        count={needsSaveCount}
        installName={installName}
        saving={pending === 'save'}
      />
    </>
  )
}

export { MediaPage }
